import React from 'react';
import { DynamicField, isPlyFieldVisible, sizeSuffix } from '@/components/DynamicFieldRenderer';
import { Route } from 'lucide-react';

/**
 * The job card as a dense spec grid — one line per field, label cell beside
 * value cell, the way TrueERP's Product Details screen reads.
 *
 * The point is not fewer fields; it is that a blank field stops looking like an
 * unfinished task. Forty-odd fields laid out as forty boxed inputs behind a
 * progress bar reads as forty things somebody forgot. The same forty as tabbed
 * rows in two columns fit on one screen and read as a spec sheet, most of which
 * is filled in later by whoever knows the answer.
 *
 * Two marks earn their place: a field the pipeline routes on (its value decides
 * whether a process happens at all) and a field that came from the order.
 */
export default function SpecGrid({
  fields, values, onChange, autoFilledFields = [], routingFields = new Set(),
  onPlyChange, blanksOnly = false, columns = 2, orderType,
}) {
  // Every field is shown. Fields appearing and vanishing as you filled the one
  // above was the worst of the old card: you could not see what the job needed
  // until you had half-answered it, and a value you had typed could disappear
  // when something else changed. Ply layers are the one exception — a 3-ply
  // board has no layer 5 to describe.
  const visible = fields
    .filter(field => isPlyFieldVisible(field, values))
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const isBlank = (field) => {
    const value = values[field.name];
    return value === undefined || value === null || value === '' ||
      (typeof value === 'string' && ['none', 'n/a'].includes(value.toLowerCase())) ||
      (Array.isArray(value) && value.length === 0);
  };

  const rows = blanksOnly ? visible.filter(isBlank) : visible;

  if (rows.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-xs text-slate-400">
        {blanksOnly ? 'Nothing left blank here.' : 'No fields for this product type.'}
      </p>
    );
  }

  return (
    <div className={`grid ${columns === 2 ? 'lg:grid-cols-2' : ''} gap-x-5`}>
      {rows.map(field => {
        const autoFilled = autoFilledFields.includes(field.name);
        const routes = routingFields.has(field.name);
        const label = field.label?.replace(/\s*\([^)]*\)\s*$/, '') || field.name;
        const unitSuffix = field.name === 'size' ? sizeSuffix(orderType) : '';

        return (
          <div
            key={field.name}
            className="grid grid-cols-[10rem_1fr] items-stretch border-b border-slate-200"
          >
            <div
              className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium border-r border-slate-200 ${
                routes ? 'bg-emerald-50 text-emerald-900' : 'bg-slate-100 text-slate-700'
              }`}
              title={routes ? 'This field decides whether a process runs' : undefined}
            >
              {routes && <Route className="h-3 w-3 shrink-0 text-emerald-600" />}
              <span className="truncate">
                {label}{unitSuffix}
                {field.required && <span className="text-brand-gold-deep ml-0.5">*</span>}
              </span>
            </div>
            <div className="px-1.5 py-0.5 flex items-center gap-1.5 min-w-0">
              <div className="min-w-0 flex-1">
                <DynamicField
                  field={field}
                  value={values[field.name]}
                  onChange={(val) =>
                    field.name === 'ply' && onPlyChange ? onPlyChange(val) : onChange(field.name, val)
                  }
                  autoFilled={autoFilled}
                  compact
                  hideLabel
                  orderType={orderType}
                />
              </div>
              {autoFilled && !isBlank(field) && (
                <span className="shrink-0 text-[9px] text-blue-600" title="Filled in from the order">
                  order
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
